import React, { useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { toast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const LIST_PROVIDERS = gql`
  query ListProviders {
    listProviders {
      uniqueid
      provider
      code
      to_address
      cc_address
      reply_address
      deprecated
      programs
      facility
      for_use
      for_use_specific
      alias
      programIds
    }
  }
`;

const LIST_PROGRAMS = gql`
  query ListPrograms {
    listPrograms {
      id
      name
      slug
      active
    }
  }
`;

const CREATE_PROVIDER = gql`
  mutation CreateProvider($updates: ProviderInput) {
    createProvider(updates: $updates) {
      uniqueid
      provider
      code
      to_address
      cc_address
      reply_address
      deprecated
      programs
      facility
      for_use
      for_use_specific
      alias
      programIds
    }
  }
`;

const UPDATE_PROVIDER = gql`
  mutation UpdateProvider($id: Int, $updates: ProviderInput) {
    updateProvider(id: $id, updates: $updates) {
      uniqueid
      provider
      code
      to_address
      cc_address
      reply_address
      deprecated
      programs
      facility
      for_use
      for_use_specific
      alias
      programIds
    }
  }
`;

const DELETE_PROVIDER = gql`
  mutation DeleteProvider($id: Int) {
    deleteProvider(id: $id) {
      message
      responseCode
    }
  }
`;

type ProviderRecord = {
  uniqueid: number;
  provider?: string | null;
  code?: string | null;
  to_address?: string | null;
  cc_address?: string | null;
  reply_address?: string | null;
  deprecated?: boolean | null;
  programs?: string | null;
  facility?: string | null;
  for_use?: string | null;
  for_use_specific?: string | null;
  alias?: string | null;
  programIds?: number[] | null;
};

type ProgramRecord = {
  id: number;
  name?: string | null;
  slug?: string | null;
  active?: number | null;
};

type ProviderFormState = {
  uniqueid: number | null;
  provider: string;
  code: string;
  to_address: string;
  cc_address: string;
  reply_address: string;
  deprecated: boolean;
  programs: string;
  facility: string;
  for_use: string;
  for_use_specific: string;
  alias: string;
  programIds: number[];
};

const emptyProvider: ProviderFormState = {
  uniqueid: null,
  provider: '',
  code: '',
  to_address: '',
  cc_address: '',
  reply_address: '',
  deprecated: false,
  programs: '',
  facility: '',
  for_use: '',
  for_use_specific: '',
  alias: '',
  programIds: []
};

const normalizeProvider = (provider: ProviderRecord): ProviderFormState => {
  return {
    uniqueid: provider.uniqueid,
    provider: provider.provider || '',
    code: provider.code || '',
    to_address: provider.to_address || '',
    cc_address: provider.cc_address || '',
    reply_address: provider.reply_address || '',
    deprecated: Boolean(provider.deprecated),
    programs: provider.programs || '',
    facility: provider.facility || '',
    for_use: provider.for_use || '',
    for_use_specific: provider.for_use_specific || '',
    alias: provider.alias || '',
    programIds: Array.isArray(provider.programIds) ? provider.programIds : []
  };
};

type ProvidersManagerProps = {
  embedded?: boolean;
};

export const ProvidersManager: React.FC<ProvidersManagerProps> = ({ embedded = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [formState, setFormState] = useState<ProviderFormState>(emptyProvider);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data: providersData,
    loading: providersLoading,
    error: providersError,
    refetch: refetchProviders
  } = useQuery<{ listProviders: ProviderRecord[] }>(LIST_PROVIDERS, {
    fetchPolicy: 'cache-and-network'
  });

  const {
    data: programsData,
    loading: programsLoading,
    error: programsError
  } = useQuery<{ listPrograms: ProgramRecord[] }>(LIST_PROGRAMS, {
    fetchPolicy: 'cache-and-network'
  });

  const [createProvider] = useMutation(CREATE_PROVIDER);
  const [updateProvider] = useMutation(UPDATE_PROVIDER);
  const [deleteProvider] = useMutation(DELETE_PROVIDER);

  const programs = useMemo(() => {
    const rows = programsData?.listPrograms;
    if (!Array.isArray(rows)) {
      return [];
    }
    return [...rows].sort((a, b) => {
      const left = a.name || a.slug || '';
      const right = b.name || b.slug || '';
      return left.localeCompare(right);
    });
  }, [programsData]);

  const programLabelById = useMemo(() => {
    const map = new Map<number, string>();
    programs.forEach((program) => {
      const label = program.slug || program.name || String(program.id);
      map.set(program.id, label);
    });
    return map;
  }, [programs]);

  const providers = useMemo(() => {
    const rows = providersData?.listProviders;
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows
      .map((row) => normalizeProvider(row))
      .sort((a, b) => {
        const left = a.code || a.provider;
        const right = b.code || b.provider;
        return left.localeCompare(right);
      });
  }, [providersData]);

  const filteredProviders = useMemo(() => {
    if (!searchQuery) {
      return providers;
    }

    const query = searchQuery.toLowerCase();
    return providers.filter((provider) => {
      const code = provider.code.toLowerCase();
      const name = provider.provider.toLowerCase();
      const alias = provider.alias.toLowerCase();
      return code.includes(query) || name.includes(query) || alias.includes(query);
    });
  }, [providers, searchQuery]);

  const handleSelectProvider = (provider: ProviderFormState) => {
    setFormState(provider);
  };

  const handleNewProvider = () => {
    setFormState(emptyProvider);
    setIsDialogOpen(true);
  };

  const handleEditProvider = (provider: ProviderFormState) => {
    handleSelectProvider(provider);
    setIsDialogOpen(true);
  };

  const handleFieldChange = (field: keyof ProviderFormState, value: string | boolean) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProgramToggle = (programId: number, checked: boolean) => {
    setFormState((prev) => {
      const next = new Set(prev.programIds);
      if (checked) {
        next.add(programId);
      }
      if (!checked) {
        next.delete(programId);
      }
      return {
        ...prev,
        programIds: Array.from(next)
      };
    });
  };

  const buildProviderInput = (provider: ProviderFormState) => {
    return {
      provider: provider.provider || null,
      code: provider.code || null,
      to_address: provider.to_address || null,
      cc_address: provider.cc_address || null,
      reply_address: provider.reply_address || null,
      deprecated: provider.deprecated,
      programs: provider.programs || null,
      facility: provider.facility || null,
      for_use: provider.for_use || null,
      for_use_specific: provider.for_use_specific || null,
      alias: provider.alias || null,
      programIds: provider.programIds
    };
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const input = buildProviderInput(formState);

      if (formState.uniqueid) {
        const result = await updateProvider({
          variables: { id: formState.uniqueid, updates: input }
        });
        const updated = result?.data?.updateProvider;
        if (updated) {
          setFormState(normalizeProvider(updated));
        }
        toast.success('Provider updated');
      }

      if (!formState.uniqueid) {
        const result = await createProvider({ variables: { updates: input } });
        const created = result?.data?.createProvider;
        if (created) {
          setFormState(normalizeProvider(created));
        }
        toast.success('Provider created');
      }

      await refetchProviders();
    } catch (error: any) {
      console.error('Failed to save provider:', error);
      toast.error(error?.message || 'Failed to save provider');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!formState.uniqueid) {
      return;
    }

    setIsDeleting(true);

    try {
      const result = await deleteProvider({ variables: { id: formState.uniqueid } });
      const ok = result?.data?.deleteProvider?.responseCode === 0;
      if (!ok) {
        toast.error(result?.data?.deleteProvider?.message || 'Failed to delete provider');
        return;
      }
      toast.success('Provider deleted');
      setFormState(emptyProvider);
      await refetchProviders();
    } catch (error: any) {
      console.error('Failed to delete provider:', error);
      toast.error(error?.message || 'Failed to delete provider');
    } finally {
      setIsDeleting(false);
    }
  };

  if (providersLoading || programsLoading) {
    return <div className={embedded ? 'text-sm text-gray-500' : 'p-6 text-sm text-gray-500'}>Loading providers...</div>;
  }

  if (providersError) {
    return <div className={embedded ? 'text-sm text-red-600' : 'p-6 text-sm text-red-600'}>Failed to load providers.</div>;
  }

  if (programsError) {
    return <div className={embedded ? 'text-sm text-red-600' : 'p-6 text-sm text-red-600'}>Failed to load programs.</div>;
  }

  return (
    <div className={embedded ? 'space-y-4' : 'p-6 space-y-6'}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className={embedded ? 'text-lg font-semibold text-gray-900' : 'text-2xl font-semibold text-gray-900'}>Provider Settings</h1>
          <p className="text-sm text-gray-500">Manage provider options and program assignments.</p>
        </div>
        <Button variant="secondary" onClick={handleNewProvider}>
          New Provider
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Providers</CardTitle>
            <CardDescription>Search and select a provider to edit.</CardDescription>
            <div className="pt-4">
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by code, name, or alias"
              />
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Programs</TableHead>
                  <TableHead>Deprecated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProviders.map((provider) => {
                  const programLabels = provider.programIds
                    .map((id) => programLabelById.get(id) || String(id))
                    .join(', ');
                  const isSelected = provider.uniqueid === formState.uniqueid;
                  return (
                    <TableRow
                      key={provider.uniqueid}
                      className={isSelected ? 'bg-blue-50 cursor-pointer' : 'cursor-pointer'}
                      onClick={() => handleEditProvider(provider)}
                    >
                      <TableCell className="font-mono text-xs">{provider.code || '-'}</TableCell>
                      <TableCell>{provider.provider || '-'}</TableCell>
                      <TableCell className="text-xs text-gray-500">{programLabels || '-'}</TableCell>
                      <TableCell>{provider.deprecated ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[90vw] sm:w-[90vw] max-w-none sm:max-w-[90vw] h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>{formState.uniqueid ? 'Edit Provider' : 'New Provider'}</DialogTitle>
            <DialogDescription>Update provider details and link programs.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="provider-name">Provider Name</Label>
                    <Input
                      id="provider-name"
                      value={formState.provider}
                      onChange={(event) => handleFieldChange('provider', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="provider-code">Code</Label>
                    <Input
                      id="provider-code"
                      value={formState.code}
                      onChange={(event) => handleFieldChange('code', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="provider-alias">Alias</Label>
                    <Input
                      id="provider-alias"
                      value={formState.alias}
                      onChange={(event) => handleFieldChange('alias', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="provider-facility">Facility</Label>
                    <Input
                      id="provider-facility"
                      value={formState.facility}
                      onChange={(event) => handleFieldChange('facility', event.target.value)}
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="provider-for-use">For Use</Label>
                    <Input
                      id="provider-for-use"
                      value={formState.for_use}
                      onChange={(event) => handleFieldChange('for_use', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="provider-for-use-specific">For Use Specific</Label>
                    <Input
                      id="provider-for-use-specific"
                      value={formState.for_use_specific}
                      onChange={(event) => handleFieldChange('for_use_specific', event.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="provider-programs">Programs (legacy text)</Label>
                  <Input
                    id="provider-programs"
                    value={formState.programs}
                    onChange={(event) => handleFieldChange('programs', event.target.value)}
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="provider-deprecated">Deprecated</Label>
                  <Switch
                    id="provider-deprecated"
                    checked={formState.deprecated}
                    onCheckedChange={(checked) => handleFieldChange('deprecated', Boolean(checked))}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="provider-to-address">To Address</Label>
                  <Textarea
                    id="provider-to-address"
                    rows={3}
                    value={formState.to_address}
                    onChange={(event) => handleFieldChange('to_address', event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="provider-cc-address">CC Address</Label>
                  <Textarea
                    id="provider-cc-address"
                    rows={3}
                    value={formState.cc_address}
                    onChange={(event) => handleFieldChange('cc_address', event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="provider-reply-address">Reply Address</Label>
                  <Textarea
                    id="provider-reply-address"
                    rows={3}
                    value={formState.reply_address}
                    onChange={(event) => handleFieldChange('reply_address', event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Program Links</Label>
                  <div className="border rounded-md p-3 max-h-72 overflow-y-auto">
                    <div className="grid gap-2 sm:grid-cols-2">
                      {programs.map((program) => {
                        const label = program.slug || program.name || String(program.id);
                        const checked = formState.programIds.includes(program.id);
                        return (
                          <label key={program.id} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(value) => handleProgramToggle(program.id, Boolean(value))}
                            />
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                    {programs.length === 0 && (
                      <div className="text-xs text-gray-500">No programs found.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-4 border-t flex items-center justify-between gap-3">
            <div className="text-xs text-gray-500">
              {formState.uniqueid ? `Provider ID ${formState.uniqueid}` : 'New provider'}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Close
              </Button>
              {formState.uniqueid && (
                <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProvidersManager;
